import { useState } from 'react';
import { ListBox, ListBoxItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { ClientFormDrawer } from '@/components/ClientForm/ClientFormDrawer';
import { Link } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { ClientFromGetMany, useClients } from '@/models/client';
import { formatPhone } from '@/models/partials/phone';
import useDebouncedState from '@/utils/useDebouncedState';

import './AddClientModal.css';

interface AddClientModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectClient: (client: ClientFromGetMany) => void;
}

export const AddClientModal = ({ isOpen, onOpenChange, onSelectClient }: AddClientModalProps) => {
  const { t } = useLingui();
  const [selectedClient, setSelectedClient] = useState<ClientFromGetMany | null>(null);
  const [isCreateClientDrawerOpen, setIsCreateClientDrawerOpen] = useState(false);

  const [code, debouncedCode, setCode] = useDebouncedState('', 300);
  const [lastName, debouncedLastName, setLastName] = useDebouncedState('', 300);
  const [firstName, debouncedFirstName, setFirstName] = useDebouncedState('', 300);
  const [postalCode, debouncedPostalCode, setPostalCode] = useDebouncedState('', 300);

  const { data: { clients } = { clients: [] }, isFetching } = useClients({
    code: debouncedCode,
    firstName: debouncedFirstName,
    lastName: debouncedLastName,
    postalCode: debouncedPostalCode,
  });

  const handleAddClient = () => {
    if (selectedClient) {
      onSelectClient(selectedClient);
      setSelectedClient(null);
      onOpenChange(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setSelectedClient(null);
    }
    onOpenChange(open);
  };

  return (
    <>
      <Dialog
        title={
          <Trans id="requests.client-manager.add-client-modal.title">Search a client to add</Trans>
        }
        isOpen={isOpen}
        onOpenChange={handleClose}
        fullHeight
      >
        <main style={{ padding: '16px 0' }}>
          <Stack gap="0" style={{ width: '100%' }}>
            <Stack gap="1rem" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
              <InputText
                label={<Trans id="client-search-input.code.label">Code:</Trans>}
                value={code}
                onChange={(value) => setCode(value)}
                placeholder={t({
                  id: 'client-search-input.field-input.placeholder',
                  message: 'Search...',
                })}
              />
              <InputText
                label={<Trans id="client-search-input.last-name.label">Last name:</Trans>}
                value={lastName}
                onChange={(value) => setLastName(value)}
                placeholder={t({
                  id: 'client-search-input.field-input.placeholder',
                  message: 'Search...',
                })}
              />
              <InputText
                label={<Trans id="client-search-input.first-name.label">First name:</Trans>}
                value={firstName}
                onChange={(value) => setFirstName(value)}
                placeholder={t({
                  id: 'client-search-input.field-input.placeholder',
                  message: 'Search...',
                })}
              />
              <InputText
                label={<Trans id="client-search-input.postal-code.label">Postal code:</Trans>}
                value={postalCode}
                onChange={(value) => setPostalCode(value)}
                placeholder={t({
                  id: 'client-search-input.field-input.placeholder',
                  message: 'Search...',
                })}
              />
            </Stack>
            <div className="add-client-modal__separator" />
            <Stack
              gap="0"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                paddingTop: '1rem',
              }}
            >
              {isFetching && (
                <Stack alignItems="center" justifyContent="center" gap="1rem">
                  <Paragraph color="secondary">
                    <Trans id="_general.loading">Loading...</Trans>
                  </Paragraph>
                </Stack>
              )}
              {!isFetching && (
                <ListBox
                  aria-label={t({
                    id: 'requests.client-manager.add-client-modal.results.aria-label',
                    message: 'Client search results',
                  })}
                  className="add-client-modal__results"
                  items={clients}
                  selectionMode="single"
                  selectedKeys={selectedClient ? [selectedClient.externalId] : []}
                  onSelectionChange={(keys) => {
                    const key = keys instanceof Set ? keys.values().next().value : undefined;

                    setSelectedClient(
                      key ? (clients.find((c) => c.externalId === key) ?? null) : null
                    );
                  }}
                >
                  {(client) => (
                    <ListBoxItem
                      id={client.externalId}
                      className="add-client-modal__result"
                      textValue={client.name ?? ''}
                    >
                      <Paragraph weight="medium">{client.name ?? '-'}</Paragraph>
                      <Paragraph size="300" color="secondary">
                        {[client.email, formatPhone(client.phone), client.address?.zipCode]
                          .filter(Boolean)
                          .join(' • ')}
                      </Paragraph>
                    </ListBoxItem>
                  )}
                </ListBox>
              )}
            </Stack>
          </Stack>
        </main>
        <Footer>
          <Stack
            alignItems="center"
            justifyContent="space-between"
            gap="1rem"
            row
            style={{ flex: 1 }}
          >
            <Paragraph size="200" color="secondary">
              <Trans id="client-search-input.cant-find-them">
                Can&apos;t find them?{' '}
                <Link
                  to="#"
                  onClick={() => {
                    onOpenChange(false);
                    setIsCreateClientDrawerOpen(true);
                  }}
                >
                  Create a new client
                </Link>
              </Trans>
            </Paragraph>
            <FooterButtons>
              <Button variant="primary" onPress={handleAddClient}>
                <Trans id="requests.new.client.add">Add a client</Trans>
              </Button>
            </FooterButtons>
          </Stack>
        </Footer>
      </Dialog>

      <ClientFormDrawer
        isOpen={isCreateClientDrawerOpen}
        setIsOpen={setIsCreateClientDrawerOpen}
        hideDeliveryOption
        customTitle={
          <Trans id="client-search-input.create-client-drawer.title">Create a new client</Trans>
        }
      />
    </>
  );
};
